import React, { useState, useEffect, useRef, useCallback } from 'react';
import './DodgeSurvivalGame.css';

// Game constants
const STAGE_W = 640;
const STAGE_H = 360;

const PLAYER_SIZE = 110;
const PLAYER_Y = STAGE_H - PLAYER_SIZE - 10; // Position Scout at bottom with 10px gap
const PLAYER_SPEED = 280; // px/sec
const PLAYER_HITBOX_W = PLAYER_SIZE * 0.60;
const PLAYER_HITBOX_H = PLAYER_SIZE * 0.50;

const GAME_DURATION = 60; // 60 seconds

const OBSTACLE_SIZES = {
  papers: { w: 72, h: 72 },
  book: { w: 68, h: 68 },
  clock: { w: 64, h: 64 },
};

// Difficulty settings - Alan's exact specifications
const SPAWN_INTERVAL_START = 650;  // ms
const SPAWN_INTERVAL_MIN = 220;    // ms (safety cap)
const FALL_SPEED_START = 2.5;      // base speed
const FALL_SPEED_MAX = 8.0;        // safety cap
const DIFFICULTY_RAMP_INTERVAL = 10; // seconds between difficulty increases

const COLLISION_PENALTY = -5;
const INVINCIBILITY_DURATION = 150; // ms
const DODGE_REWARD = 5; // points for successfully dodging (obstacle passes bottom)
const SURVIVAL_REWARD = 1; // points per second survived

/**
 * Dodge Survival Mini-Game Component
 * 
 * Props:
 * - isActive: boolean - controls game loop (pause when false)
 * - onScoreChange: (score) => void - called when score updates
 * - onGameEnd: (finalScore) => void - called when game ends
 * - currentScore: number - current score from parent
 * - onExit: () => void - called when exit button is clicked
 * - showFinalScore: boolean - whether to show final score
 * - finalScoreData: object - final score data
 */
export default function DodgeSurvivalGame({ 
  isActive = true, 
  onScoreChange, 
  onGameEnd,
  currentScore = 0,
  onExit,
  showFinalScore = false,
  finalScoreData = null,
  isCompact = false,
  contentReady = false,
  contentTypeName = '',
  onViewContent
}) {
  // Adjust constants for compact mode
  const EFFECTIVE_PLAYER_Y = isCompact ? STAGE_H - PLAYER_SIZE - 20 : PLAYER_Y; // Position with 20px gap in compact
  const EFFECTIVE_PLAYER_SPEED = isCompact ? 480 : PLAYER_SPEED; // Much faster in compact
  const EFFECTIVE_COLLISION_OFFSET_Y = isCompact ? 70 : 0; // Very large offset for visual overlap
  
  // Game state
  const [gameState, setGameState] = useState('instructions'); // 'instructions' | 'countdown' | 'playing' | 'gameover'
  const [countdownNumber, setCountdownNumber] = useState(3);
  const [score, setScore] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(GAME_DURATION);
  const [highScore, setHighScore] = useState(0);
  const [playerX, setPlayerX] = useState(STAGE_W / 2 - PLAYER_SIZE / 2);
  const [, setPlayerDirection] = useState(0); // -1 left, 0 idle, 1 right
  const [obstacles, setObstacles] = useState([]);
  const [popups, setPopups] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [isInvincible, setIsInvincible] = useState(false);
  const [shakeOffset, setShakeOffset] = useState({ x: 0, y: 0 });
  
  // Refs for animation loop
  const rafIdRef = useRef(null);
  const lastFrameRef = useRef(performance.now());
  const spawnIntervalRef = useRef(null);
  const gameStartTimeRef = useRef(performance.now());
  const lastDifficultyRampRef = useRef(0);
  const lastSurvivalScoreRef = useRef(0);
  const isPausedRef = useRef(false);
  const stageRef = useRef(null);
  const stageWidthRef = useRef(640);
  const lastObstacleTypeRef = useRef(null);
  const isInvincibleRef = useRef(false);
  const invincibilityEndRef = useRef(0);
  const flickerStateRef = useRef(false);
  const playerXRef = useRef(STAGE_W / 2 - PLAYER_SIZE / 2);
  const pendingCollisionRef = useRef(false);
  
  // Input state
  const keysRef = useRef({ left: false, right: false });
  const touchDirectionRef = useRef(0);

  // Notify parent of score changes
  useEffect(() => {
    if (onScoreChange) {
      onScoreChange(score);
    }
  }, [score, onScoreChange]);

  // Load high score on mount
  useEffect(() => {
    const saved = localStorage.getItem('dodgeSurvivalHighScore');
    if (saved) {
      setHighScore(parseInt(saved, 10));
    }
  }, []);

  // Track stage width for speed scaling
  useEffect(() => {
    const updateStageWidth = () => {
      if (stageRef.current) {
        stageWidthRef.current = stageRef.current.offsetWidth;
      }
    };
    
    const timeoutId = setTimeout(updateStageWidth, 0);
    window.addEventListener('resize', updateStageWidth);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateStageWidth);
    };
  }, [gameState]);

  // Update high score if current score exceeds it
  useEffect(() => {
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('dodgeSurvivalHighScore', score.toString());
    }
  }, [score, highScore]);

  // Game timer countdown
  useEffect(() => {
    if (gameState !== 'playing') return;

    const timerInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          setGameState('gameover');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [gameState]);

  // Handle start game button
  const handleStartGame = useCallback(() => {
    setGameState('countdown');
    setCountdownNumber(3);
    setTimeRemaining(GAME_DURATION);
    setScore(0);
    setObstacles([]);
    setPlayerX(STAGE_W / 2 - PLAYER_SIZE / 2);
    setIsInvincible(false);
    isInvincibleRef.current = false;
    invincibilityEndRef.current = 0;
    lastObstacleTypeRef.current = null;
    lastDifficultyRampRef.current = 0;
    lastSurvivalScoreRef.current = 0;
    
    // Countdown sequence
    let count = 3;
    const countdownInterval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdownNumber(count);
      } else {
        clearInterval(countdownInterval);
        setGameState('playing');
      }
    }, 1000);
  }, []);

  // Handle play again
  const handlePlayAgain = useCallback(() => {
    handleStartGame();
  }, [handleStartGame]);

  // Calculate current difficulty based on game time
  const getCurrentDifficulty = useCallback(() => {
    if (isPausedRef.current) return null;
    
    const elapsed = (performance.now() - gameStartTimeRef.current) / 1000;
    
    // Calculate difficulty ramps (every 10 seconds)
    const numRamps = Math.floor(elapsed / DIFFICULTY_RAMP_INTERVAL);
    
    // Apply ramping: spawnInterval *= 0.88, fallSpeed += 0.25
    let spawnInterval = SPAWN_INTERVAL_START;
    let fallSpeed = FALL_SPEED_START;
    
    for (let i = 0; i < numRamps; i++) {
      spawnInterval *= 0.88;
      fallSpeed += 0.25;
    }
    
    // Apply safety caps
    spawnInterval = Math.max(spawnInterval, SPAWN_INTERVAL_MIN);
    fallSpeed = Math.min(fallSpeed, FALL_SPEED_MAX);
    
    return {
      spawnInterval,
      fallSpeed,
      elapsed,
      numRamps,
    };
  }, []);

  // Spawn a new falling obstacle with anti-streak logic
  const spawnObstacle = useCallback(() => {
    const difficulty = getCurrentDifficulty();
    if (!difficulty) return;
    
    // Random obstacle type with anti-streak rule
    const obstacleTypes = ['papers', 'book', 'clock'];
    let obstacleType = obstacleTypes[Math.floor(Math.random() * obstacleTypes.length)];
    
    // Anti-streak: reroll once if matches last type
    if (obstacleType === lastObstacleTypeRef.current) {
      obstacleType = obstacleTypes[Math.floor(Math.random() * obstacleTypes.length)];
    }
    
    lastObstacleTypeRef.current = obstacleType;
    
    const obstacleData = OBSTACLE_SIZES[obstacleType];
    const newObstacle = {
      id: Date.now() + Math.random(),
      type: obstacleType,
      x: Math.random() * (STAGE_W - obstacleData.w),
      y: -obstacleData.h,
      speed: difficulty.fallSpeed,
    };
    
    setObstacles(prev => [...prev, newObstacle]);
  }, [getCurrentDifficulty]);

  // Check AABB collision
  const checkCollision = useCallback((objX, objY, objW, objH) => {
    const hitboxX = playerX + (PLAYER_SIZE - PLAYER_HITBOX_W) / 2;
    const hitboxY = EFFECTIVE_PLAYER_Y + (PLAYER_SIZE - PLAYER_HITBOX_H) / 2 + EFFECTIVE_COLLISION_OFFSET_Y;
    
    return (
      objX < hitboxX + PLAYER_HITBOX_W &&
      objX + objW > hitboxX &&
      objY < hitboxY + PLAYER_HITBOX_H &&
      objY + objH > hitboxY
    );
  }, [playerX, EFFECTIVE_PLAYER_Y, EFFECTIVE_COLLISION_OFFSET_Y]);

  // Add floating popup
  const addPopup = useCallback((text, x, y) => {
    const popup = {
      id: Date.now() + Math.random(),
      text,
      x,
      y,
      createdAt: performance.now(),
    };
    setPopups(prev => [...prev, popup]);
    
    setTimeout(() => {
      setPopups(prev => prev.filter(p => p.id !== popup.id));
    }, 800);
  }, []);

  // Trigger screen shake
  const triggerScreenShake = useCallback(() => {
    const shakeIntensity = 3; // pixels
    const shakeDuration = 200; // ms
    const shakeSteps = 8;
    const stepDuration = shakeDuration / shakeSteps;
    
    let step = 0;
    const shakeInterval = setInterval(() => {
      if (step >= shakeSteps) {
        setShakeOffset({ x: 0, y: 0 });
        clearInterval(shakeInterval);
        return;
      }
      
      // Random shake with easing (reduce intensity over time)
      const easingFactor = 1 - (step / shakeSteps);
      const offsetX = (Math.random() - 0.5) * shakeIntensity * easingFactor;
      const offsetY = (Math.random() - 0.5) * shakeIntensity * easingFactor;
      
      setShakeOffset({ x: offsetX, y: offsetY });
      step++;
    }, stepDuration);
  }, []);

  // Handle collision
  const handleCollision = useCallback((obstacleX, obstacleY) => {
    if (isInvincibleRef.current) return;
    
    // Mark that collision happened (score will be updated in game loop)
    pendingCollisionRef.current = true;
    
    // Show penalty popup at scout position
    const popupX = playerXRef.current + PLAYER_SIZE / 2;
    const popupY = PLAYER_Y - 10;
    addPopup(`${COLLISION_PENALTY}`, popupX, popupY);
    
    // Trigger screen shake
    triggerScreenShake();
    
    // Apply invincibility
    isInvincibleRef.current = true;
    setIsInvincible(true);
    invincibilityEndRef.current = performance.now() + INVINCIBILITY_DURATION;
    
    setTimeout(() => {
      isInvincibleRef.current = false;
      setIsInvincible(false);
    }, INVINCIBILITY_DURATION);
  }, [addPopup, triggerScreenShake]);

  // Game loop
  const gameLoop = useCallback(() => {
    if (!isActive || isPausedRef.current || gameState !== 'playing') {
      return;
    }
    
    const now = performance.now();
    let deltaTime = (now - lastFrameRef.current) / 1000;
    lastFrameRef.current = now;
    
    deltaTime = Math.min(deltaTime, 0.1);
    
    const difficulty = getCurrentDifficulty();
    if (!difficulty) return;
    
    // Award survival points (1 per second)
    const currentSecond = Math.floor(difficulty.elapsed);
    if (currentSecond > lastSurvivalScoreRef.current) {
      lastSurvivalScoreRef.current = currentSecond;
      setScore(prevScore => prevScore + SURVIVAL_REWARD);
    }
    
    // Update player position
    const direction = touchDirectionRef.current || (keysRef.current.left ? -1 : keysRef.current.right ? 1 : 0);
    setPlayerDirection(direction);
    
    if (direction !== 0) {
      const speedMultiplier = stageWidthRef.current / STAGE_W;
      setPlayerX(prev => {
        const newX = prev + direction * EFFECTIVE_PLAYER_SPEED * speedMultiplier * deltaTime;
        const clampedX = Math.max(0, Math.min(STAGE_W - PLAYER_SIZE, newX));
        playerXRef.current = clampedX;
        return clampedX;
      });
    }
    
    // Update invincibility flicker
    if (isInvincibleRef.current) {
      flickerStateRef.current = !flickerStateRef.current;
    }
    
    // Update falling obstacles
    setObstacles(prev => {
      const updated = [];
      
      const speedMultiplier = stageWidthRef.current / STAGE_W;
      
      for (const obj of prev) {
        const obstacleData = OBSTACLE_SIZES[obj.type];
        const newY = obj.y + obj.speed * 60 * speedMultiplier * deltaTime; // speed is per-frame multiplier
        
        // Check collision (only if not invincible)
        if (!isInvincibleRef.current && checkCollision(obj.x, newY, obstacleData.w, obstacleData.h)) {
          handleCollision(obj.x, newY);
          continue; // Remove obstacle
        }
        
        // Check if successfully dodged (passed bottom)
        if (newY > STAGE_H) {
          setScore(prevScore => prevScore + DODGE_REWARD);
          continue; // Remove obstacle
        }
        
        updated.push({ ...obj, y: newY });
      }
      
      return updated;
    });
    
    // Apply collision penalty if one occurred this frame
    if (pendingCollisionRef.current) {
      pendingCollisionRef.current = false;
      setScore(prevScore => Math.max(0, prevScore + COLLISION_PENALTY));
    }
    
    rafIdRef.current = requestAnimationFrame(gameLoop);
  }, [isActive, checkCollision, handleCollision, getCurrentDifficulty, gameState, EFFECTIVE_PLAYER_SPEED]);

  // Pause/resume handlers
  const pauseGame = useCallback(() => {
    isPausedRef.current = true;
    setIsPaused(true);
    
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    
    if (spawnIntervalRef.current) {
      clearInterval(spawnIntervalRef.current);
      spawnIntervalRef.current = null;
    }
  }, []);

  const resumeGame = useCallback(() => {
    isPausedRef.current = false;
    setIsPaused(false);
    lastFrameRef.current = performance.now();
    
    const difficulty = getCurrentDifficulty();
    if (difficulty && !spawnIntervalRef.current) {
      spawnIntervalRef.current = setInterval(() => {
        const currentDifficulty = getCurrentDifficulty();
        if (currentDifficulty) {
          spawnObstacle();
        }
      }, difficulty.spawnInterval);
    }
    
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(gameLoop);
    }
  }, [gameLoop, spawnObstacle, getCurrentDifficulty]);

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        keysRef.current.left = true;
        e.preventDefault();
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        keysRef.current.right = true;
        e.preventDefault();
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        keysRef.current.left = false;
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        keysRef.current.right = false;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Tab visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        pauseGame();
      } else if (isActive) {
        resumeGame();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, pauseGame, resumeGame]);

  // Start/stop game loop based on isActive prop and game state
  useEffect(() => {
    if (isActive && !isPausedRef.current && gameState === 'playing') {
      if (!rafIdRef.current) {
        gameStartTimeRef.current = performance.now();
        lastFrameRef.current = performance.now();
      }
      
      const difficulty = getCurrentDifficulty() || { spawnInterval: SPAWN_INTERVAL_START };
      spawnIntervalRef.current = setInterval(() => {
        spawnObstacle();
      }, difficulty.spawnInterval);
      
      rafIdRef.current = requestAnimationFrame(gameLoop);
    } else {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current);
        spawnIntervalRef.current = null;
      }
    }
    
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current);
      }
    };
  }, [isActive, gameLoop, spawnObstacle, getCurrentDifficulty, gameState]);

  // Touch/pointer handlers for mobile
  const handlePointerDown = (e, side) => {
    touchDirectionRef.current = side === 'left' ? -1 : 1;
    e.preventDefault();
  };

  const handlePointerUp = () => {
    touchDirectionRef.current = 0;
  };

  // Get player sprite (using single stance sprite)
  const getPlayerSprite = () => {
    return '/scout_images/games/dodge_survival/scout_survival_stance.png';
  };

  // Get obstacle sprite
  const getObstacleSprite = (type) => {
    const sprites = {
      papers: '/scout_images/games/dodge_survival/exploding_papers.png',
      book: '/scout_images/games/dodge_survival/spinning_book.png',
      clock: '/scout_images/games/dodge_survival/alarm_clock_ringing.png',
    };
    return sprites[type];
  };

  return (
    <div className={`dodge-survival-game ${isCompact ? 'dodge-survival-game--compact' : ''}`}>
      <button className="game-exit-overlay" onClick={onExit}>
        ✕
      </button>
      
      {gameState === 'playing' && (
        <div className="game-score-overlay">
          <div className="score-line">Score: {score}</div>
          <div className="timer-line">Time: {timeRemaining}s</div>
          <div className="highscore-line">High Score: {highScore}</div>
        </div>
      )}
      
      <div 
        ref={stageRef}
        className="game-stage"
        style={{
          backgroundImage: 'url(/scout_images/games/dodge_survival/dodge_surival_background.png)',
          transform: `translate(${shakeOffset.x}px, ${shakeOffset.y}px)`,
        }}
      >
        <div 
          className="touch-zone touch-zone-left"
          onPointerDown={(e) => handlePointerDown(e, 'left')}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        <div 
          className="touch-zone touch-zone-right"
          onPointerDown={(e) => handlePointerDown(e, 'right')}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        
        {/* Player (Scout) with invincibility effect */}
        <img
          className="player"
          src={getPlayerSprite()}
          alt="Scout"
          style={{
            left: `${(playerX / STAGE_W) * 100}%`,
            bottom: `${((STAGE_H - EFFECTIVE_PLAYER_Y - PLAYER_SIZE) / STAGE_H) * 100}%`,
            width: `${PLAYER_SIZE}px`,
            height: `${PLAYER_SIZE}px`,
            opacity: isInvincible && flickerStateRef.current ? 0.55 : 1,
          }}
        />
        
        {/* Red tint overlay on scout during invincibility */}
        {isInvincible && (
          <div
            className="player-invincibility-tint"
            style={{
              left: `${(playerX / STAGE_W) * 100}%`,
              bottom: `${((STAGE_H - EFFECTIVE_PLAYER_Y - PLAYER_SIZE) / STAGE_H) * 100}%`,
              width: `${PLAYER_SIZE}px`,
              height: `${PLAYER_SIZE}px`,
            }}
          />
        )}
        
        {/* Falling obstacles */}
        {obstacles.map(obj => {
          const obstacleData = OBSTACLE_SIZES[obj.type];
          return (
            <img
              key={obj.id}
              className={`falling-obstacle falling-${obj.type}`}
              src={getObstacleSprite(obj.type)}
              alt={obj.type}
              style={{
                left: `${(obj.x / STAGE_W) * 100}%`,
                top: `${(obj.y / STAGE_H) * 100}%`,
                width: `${obstacleData.w}px`,
                height: `${obstacleData.h}px`,
              }}
            />
          );
        })}
        
        {/* Score popups */}
        {popups.map(popup => (
          <div
            key={popup.id}
            className="score-popup"
            style={{
              left: `${(popup.x / STAGE_W) * 100}%`,
              top: `${(popup.y / STAGE_H) * 100}%`,
            }}
          >
            {popup.text}
          </div>
        ))}
        
        {isPaused && (
          <div className="paused-indicator">
            Paused
          </div>
        )}
        
        {/* Instructions screen */}
        {gameState === 'instructions' && (
          <div className="game-instructions-overlay">
            <div className="instructions-content">
              <h2>Dodge Survival</h2>
              <div className="instructions-rules">
                <p><strong>How to Play:</strong></p>
                <p className="timer-notice">Survive for <strong>60 seconds</strong> by dodging falling objects!</p>
                <ul>
                  <li>Move Scout left and right to avoid obstacles</li>
                  <li>📄 Exploding Papers</li>
                  <li>📚 Spinning Books</li>
                  <li>⏰ Alarm Clocks</li>
                  <li>Getting hit: -5 points</li>
                  <li>Surviving: +1 point per second</li>
                  <li>Successfully dodging: +5 points per obstacle</li>
                </ul>
                <p className="controls-hint">
                  <strong>Controls:</strong> Arrow keys or A/D
                </p>
              </div>
              <button className="start-game-btn" onClick={handleStartGame}>
                Start Game
              </button>
            </div>
          </div>
        )}
        
        {/* Countdown circle */}
        {gameState === 'countdown' && (
          <div className="countdown-overlay">
            <div className="countdown-circle">
              <span className="countdown-number">{countdownNumber}</span>
            </div>
          </div>
        )}

        {/* Game Over screen */}
        {gameState === 'gameover' && (
          <div className="game-over-overlay">
            <div className="game-over-content">
              <h2 className="game-over-title">
                {score >= highScore && score > 0 ? '🏆 New High Score!' : 'Game Over!'}
              </h2>
              <p className="game-over-message">
                {score >= highScore && score > 0 ? 'Great job!' : 'Good job!'}
              </p>
              <div className="game-over-stats">
                <div className="stat-item">
                  <span className="stat-label">Your Score</span>
                  <span className="stat-value">{score}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">High Score</span>
                  <span className="stat-value">{highScore}</span>
                </div>
              </div>
              <div className="game-over-buttons">
                <button className="game-over-btn play-again-btn" onClick={handlePlayAgain}>
                  Play Again
                </button>
              </div>
              {contentReady && (
                <div className="game-over-ready-banner">
                  <span>✅ Your {contentTypeName} is ready!</span>
                  <button className="game-over-btn view-content-btn" onClick={onViewContent}>
                    View {contentTypeName}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
